import type { IApi } from "../hooks/useApi"

export async function duplicateOrder(
  skeem: typeof import("./skeem").skeem,
  api: Pick<IApi, "locationsStock">,
  currentUser: { id: string; branch: { id: string } },
  orderId: string,
  useExplicitAllocations?: boolean,
) {
  const [order] = await skeem.fetch<{
    source_branch_id: string
    carriage_pennies: number
    delivery_method: string
    purchase_order_reference: string
    customer: { id: string } | null
    contact: { id: string } | null
    address: {
      id: string
      label: string
      stored: string
      name: string
      line_1: string
      line_2: string
      line_3: string
      line_4: string
      city: string
      county: string
      country: string
      postcode: string
      phone: string
      email: string
    }
    items: {
      id: string
      body: string
      category: string
      cost_price_pennies: number
      price_pennies: number
      quantity: number
      stock_code: string
      stock_location: string
      type: number
      product: { id: string } | null
      product_location: { id: string } | null
      weight: number
      discountPercent: number
      parent_uid: string
      markUp: string
    }[]
  }>({
    orders: {
      attributes: [
        "source_branch_id",
        "carriage_pennies",
        "delivery_method",
        "purchase_order_reference",
        { name: "customer" },
        { name: "contact" },
        {
          name: "address",
          attributes: [
            "label",
            "stored",
            "name",
            "line_1",
            "line_2",
            "line_3",
            "line_4",
            "city",
            "county",
            "country",
            "postcode",
            "phone",
            "email",
          ],
        },
        {
          name: "items",
          attributes: [
            "body",
            "category",
            "cost_price_pennies",
            "price_pennies",
            "quantity",
            "stock_code",
            "stock_location",
            "type",
            "weight",
            "discountPercent",
            "parent_uid",
            "markUp",
            { name: "product" },
            { name: "product_location" },
          ],
        },
      ],
      filter: { matches: { id: orderId } },
    },
  })

  const [branchWithIdentifier] = await skeem.mutate<{ nextOrderIdentifier: number }>({
    branches: [{ update: { id: currentUser.branch.id, nextOrderIdentifier: { inc: 1 } } }],
  })

  const allowedItems = order.items.filter(item => item.product_location && item.product)
  const productLocationIds = allowedItems
    .filter(item => !!item.product_location)
    .map(v => v.product_location!.id)
  // fresh: the allocations on the new order are decided from these figures
  const stockData = await api.locationsStock(productLocationIds, {
    allocationAttribute: useExplicitAllocations ? "allocated_quantity" : "quantity",
    fresh: true,
  })

  const mutationData = {
    create: {
      identifier: String(branchWithIdentifier.nextOrderIdentifier).padStart(6, "0"),
      source_branch_id: order.source_branch_id,
      carriage_pennies: order.carriage_pennies,
      delivery_method: order.delivery_method,
      purchase_order_reference: order.purchase_order_reference,
      branch: { set: currentUser.branch.id },
      user: { set: currentUser.id },
      customer: order.customer ? { set: order.customer.id } : undefined,
      contact: order.contact ? { set: order.contact.id } : undefined,
      address: {
        create: {
          label: order.address ? order.address.label : "",
          name: order.address ? order.address.name : "",
          line_1: order.address ? order.address.line_1 : "",
          line_2: order.address ? order.address.line_2 : "",
          line_3: order.address ? order.address.line_3 : "",
          line_4: order.address ? order.address.line_4 : "",
          city: order.address ? order.address.city : "",
          county: order.address ? order.address.county : "",
          country: order.address ? order.address.country : "",
          postcode: order.address ? order.address.postcode : "",
          phone: order.address ? order.address.phone : "",
          email: order.address ? order.address.email : "",
          branch: { set: currentUser.branch.id },
        },
      },
      items: order.items.map(item => {
        let allocation: number | undefined = undefined
        // if we have a product on this item && stock data for it
        if (
          useExplicitAllocations &&
          item.product_location &&
          stockData[item.product_location.id]
        ) {
          // If we have more stock than quantity requested, we can just assign the full quantity to allocate
          // then subtract the quantity we just allocated from in stock for future items
          if (stockData[item.product_location.id].availableInStock >= (item.quantity ?? 0)) {
            allocation = item.quantity ?? 0
            stockData[item.product_location.id].availableInStock =
              stockData[item.product_location.id].availableInStock - (item.quantity ?? 0)
          } else if (stockData[item.product_location.id].availableInStock > 0) {
            // If we have stock, but not the full quantity, assign the remaining stock
            // then set in stock to 0 as we just allocated it all
            allocation = stockData[item.product_location.id].availableInStock
            stockData[item.product_location.id].availableInStock = 0
          }
        }

        return {
          create: {
            body: item.body,
            category: item.category,
            cost_price_pennies: item.cost_price_pennies,
            price_pennies: item.price_pennies,
            quantity: item.quantity,
            stock_code: item.stock_code,
            stock_location: item.stock_location,
            type: item.type,
            weight: item.weight,
            product: item.product ? { set: item.product.id } : undefined,
            product_location: item.product_location ? { set: item.product_location.id } : undefined,
            discountPercent: item.discountPercent,
            parent_uid: item.parent_uid,
            markUp: item.markUp,
            allocated_quantity: allocation,
          },
        }
      }),
    },
  }
  let duplicate: { id: string; customer?: { id: string } }
  try {
    const [duplicate_record] = await skeem.mutate<{ customer?: { id: string } }>({
      orders: [mutationData],
    })
    duplicate = duplicate_record
  } catch (e) {
    await skeem.mutate({
      alerts: {
        create: {
          type: "identifier-incremented-by-error",
          body: `An error occurred creating order which resulted in the branch identifier being incremented`,
          details: {
            source: "duplicateOrder",
            raw: mutationData,
            error: e.message,
            stack: e.stack,
          },
          notifications: ["web@cd2solutions.co.uk"],
        },
      },
    })
    throw e
  }
  return duplicate
}
